import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Updated selectors to use auth state
const selectUserRole = (state) => {
  console.log('User role from state:', state.auth?.role);
  return state.auth?.role;
};

const selectUserId = (state) => {
  console.log('User ID from state:', state.auth?.id);
  return state.auth?.id;
};

export const fetchDashboardStatus = createAsyncThunk(
  'dashboard/fetchStatus',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const userRole = selectUserRole(state);
      const userId = selectUserId(state);
      
      if (!userRole || !userId) {
        return rejectWithValue('User role or ID not found in state');
      }
      
      const statusUrl = 
        userRole === 'Project Engineer' || 
        userRole === 'Store Officer' || 
        userRole === 'PE&SO'
          ? `${API_BASE_URL}/dashboard/status2?userId=${userId}`
          : `${API_BASE_URL}/dashboard/status`;
      
      const response = await axios.get(statusUrl);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch dashboard status');
    }
  }
);

export const fetchReceiptStats = createAsyncThunk(
  'dashboard/fetchReceiptStats',
  async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/dashboard/receipt-stats`);
      console.log('Receipt stats response status:', response.status);
      
      if (!response.ok) {
        
        const errorText = await response.text();
        throw new Error(`Failed to fetch receipt stats: ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Received receipt stats:', data);
      return data;
    } catch (error) {
      console.error('Receipt stats fetch error:', error);
      throw error;
    }
  }
);

export const fetchDashboardAlerts = createAsyncThunk(
  'dashboard/fetchAlerts',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const userRole = selectUserRole(state);
      const userId = selectUserId(state);
      
      if (!userRole || !userId) {
        return rejectWithValue('User role or ID not found in state');
      }

      // Add role and userId as query parameters
      const url = new URL(`${API_BASE_URL}/dashboard/alerts`);
      url.searchParams.append('role', userRole);
      url.searchParams.append('userId', userId);

      const response = await axios.get(url.toString());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch alerts');
    }
  }
);

export const fetchIndentOpen = createAsyncThunk(
  'dashboard/fetchIndentOpen',
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const userId = selectUserId(state);
      const userRole = selectUserRole(state);
      
      console.log("Fetching indent open data with:", { userId, userRole, params });
      
      if (!userId || !userRole) {
        return rejectWithValue('User role or ID not found in state');
      }

      const queryParams = new URLSearchParams();
      
      // Add auth params
      queryParams.append('userId', userId);
      queryParams.append('role', userRole);
      
      // Add filter params if provided
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.days) queryParams.append('days', params.days);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      
      const url = `${API_BASE_URL}/dashboard/indent-open?${queryParams.toString()}`;
      console.log("Fetching from URL:", url);
      
      const response = await axios.get(url);
      console.log("API Response:", response.data);
      
      // The backend now returns a flat list, no need for complex processing
      return response.data;
    } catch (error) {
      console.error("Error fetching indent open data:", error);
      if (error.response?.status === 401) {
        return rejectWithValue('Session expired. Please login again.');
      }
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch indent open data');
    }
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState: {
    status: {
      PE: 0,
      L1: 0,
      L2: 0,
      PD: 0,
      PH: 0,
      SO: 0,
      SH: 0,
      Closed: 0
    },
    alerts: {
      default: [],
      danger: [],
      alert: [],
      warning: []
    },
    indentOpen: [],
    receiptStats: {
      CompletedItems: 0,
      PartialItems: 0,
      PendingItems: 0,
      TotalQuantityRequested: 0,
      TotalQuantityReceived: 0
    },
    loading: {
      status: false,
      receipts: false,
      alerts: false,
      indentOpen: false
    },
    error: null
  },
  reducers: {
    clearDashboardError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Receipt stats cases
      .addCase(fetchReceiptStats.pending, (state) => {
        state.loading.receipts = true;
        state.error = null;
      })
      .addCase(fetchReceiptStats.fulfilled, (state, action) => {
        state.receiptStats = action.payload;
        state.loading.receipts = false;
        state.error = null;
      })
      .addCase(fetchReceiptStats.rejected, (state, action) => {
        state.loading.receipts = false;
        state.error = action.error.message;
      })
      // Status fetch cases
      .addCase(fetchDashboardStatus.pending, (state) => {
        state.loading.status = true;
        state.error = null;
      })
      .addCase(fetchDashboardStatus.fulfilled, (state, action) => {
        state.status = action.payload;
        state.loading.status = false;
        state.error = null;
      })
      .addCase(fetchDashboardStatus.rejected, (state, action) => {
        state.loading.status = false;
        state.error = action.error.message;
      })
      // Alerts fetch cases
      .addCase(fetchDashboardAlerts.pending, (state) => {
        state.loading.alerts = true;
        state.error = null;
      })
      .addCase(fetchDashboardAlerts.fulfilled, (state, action) => {
        state.alerts = action.payload;
        state.loading.alerts = false;
        state.error = null;
      })
      .addCase(fetchDashboardAlerts.rejected, (state, action) => {
        state.loading.alerts = false;
        state.error = action.error.message;
      })
      // IndentOpen fetch cases
      .addCase(fetchIndentOpen.pending, (state) => {
        state.loading.indentOpen = true;
        state.error = null;
      })
      .addCase(fetchIndentOpen.fulfilled, (state, action) => {
        state.loading.indentOpen = false;
        state.indentOpen = action.payload;
        state.error = null;
      })
      .addCase(fetchIndentOpen.rejected, (state, action) => {
        state.loading.indentOpen = false;
        state.error = action.payload || 'Failed to fetch indent open data';
        state.indentOpen = [];
      });
  }
});

export const { clearDashboardError } = dashboardSlice.actions;
export default dashboardSlice.reducer;